'use client'

import { KanaCell, KanaScript, KanaTable, vowels, yoonVowels } from '../alphabet/kana-data'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { SelectedKana } from '../_lib/kana-practice'

type KanaTableSectionProps = {
    table: KanaTable
    selected: SelectedKana | null
    onSelect: (cell: KanaCell, script: KanaScript) => void
    cardClassName?: string
}

function KanaCard({
    cell,
    active,
    onSelect,
    className
}: {
    cell: KanaCell
    active: boolean
    onSelect: (cell: KanaCell) => void
    className?: string
}) {
    if (!cell.kana) {
        return <div className='rounded-md border border-dashed border-border px-3 py-2 text-center text-sm text-muted-foreground'></div>
    }

    return (
        <Button
            type='button'
            variant='outline'
            onClick={() => onSelect(cell)}
            className={cn(
                'h-auto whitespace-normal rounded-md px-3 py-2 text-center shadow-sm transition-all',
                'hover:-translate-y-0.5 hover:shadow',
                active
                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'bg-card text-foreground',
                className
            )}
        >
            <p className='text-2xl leading-none font-semibold tracking-tight'>{cell.kana}</p>
            <p className={cn('mt-1 text-sm', active ? 'text-primary-foreground/80' : 'text-muted-foreground')}>{cell.romaji}</p>
        </Button>
    )
}

export default function KanaTableSection({ table, selected, onSelect, cardClassName }: KanaTableSectionProps) {
    return (
        <section className='rounded-xl border border-border bg-muted/40 p-4 shadow-sm sm:p-6'>
            <h2 className='text-center text-2xl font-semibold tracking-[0.14em] text-foreground uppercase'>
                {table.title}
            </h2>

            <div className='mt-5 grid gap-6 xl:grid-cols-[2.1fr_1fr]'>
                <div>
                    <div className='mb-2 grid grid-cols-5 gap-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        {vowels.map((vowel) => (
                            <span key={`${table.title}-${vowel}`}>{vowel}</span>
                        ))}
                    </div>

                    <div className='grid gap-2'>
                        {table.baseRows.map((row, rowIndex) => (
                            <div key={`${table.title}-base-${rowIndex}`} className='grid grid-cols-5 gap-2'>
                                {row.map((cell, cellIndex) => (
                                    <KanaCard
                                        key={`${table.title}-base-${rowIndex}-${cellIndex}`}
                                        cell={cell}
                                        active={
                                            Boolean(cell.kana) &&
                                            selected?.script === table.title &&
                                            selected.cell.kana === cell.kana &&
                                            selected.cell.romaji === cell.romaji
                                        }
                                        onSelect={(selectedCell) => onSelect(selectedCell, table.title)}
                                        className={cardClassName}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className='mb-2 grid grid-cols-3 gap-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                        {yoonVowels.map((vowel) => (
                            <span key={`${table.title}-${vowel}`}>{vowel}</span>
                        ))}
                    </div>

                    <div className='grid gap-2'>
                        {table.yoonRows.map((row, rowIndex) => (
                            <div key={`${table.title}-yoon-${rowIndex}`} className='grid grid-cols-3 gap-2'>
                                {row.map((cell, cellIndex) => (
                                    <KanaCard
                                        key={`${table.title}-yoon-${rowIndex}-${cellIndex}`}
                                        cell={cell}
                                        active={
                                            Boolean(cell.kana) &&
                                            selected?.script === table.title &&
                                            selected.cell.kana === cell.kana &&
                                            selected.cell.romaji === cell.romaji
                                        }
                                        onSelect={(selectedCell) => onSelect(selectedCell, table.title)}
                                        className={cardClassName}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {table.extendedRows.length > 0 && (
                <div className='mt-5'>
                    <p className='mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
                        Phiên âm mở rộng
                    </p>
                    <div className='space-y-3'>
                        {table.extendedRows.map((extendedRow) => (
                            <div
                                key={`${table.title}-${extendedRow.id}`}
                                className='rounded-lg border border-border bg-card p-2'
                            >
                                <p className='mb-2 text-xs font-medium text-muted-foreground'>{extendedRow.label}</p>
                                <div
                                    className='grid gap-2'
                                    style={{
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))'
                                    }}
                                >
                                    {extendedRow.cells.map((cell, cellIndex) => (
                                        <KanaCard
                                            key={`${table.title}-${extendedRow.id}-${cellIndex}`}
                                            cell={cell}
                                            active={
                                                Boolean(cell.kana) &&
                                                selected?.script === table.title &&
                                                selected.cell.kana === cell.kana &&
                                                selected.cell.romaji === cell.romaji
                                            }
                                            onSelect={(selectedCell) => onSelect(selectedCell, table.title)}
                                            className={cardClassName}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    )
}
